import { CandidateDTO, mapToCandidate } from 'src/app/dtos';
import {
  Candidate,
  CandidateCreationDescriptor,
  CandidateDescriptor,
  Manager,
  Page,
  PageRequest,
} from '$synapse/models';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { genericBaseUrl } from './utils';

@Injectable({
  providedIn: 'root',
})
export class CandidateService {
  private readonly _http = inject(HttpClient);
  private readonly _toastr = inject(ToastrService);

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };
  manager: Manager;

  baseUrl(...path: any[]): string {
    return genericBaseUrl(
      `${environment.settings.api.url}/manager/candidates`,
      ...path
    );
  }

  findAll(page: PageRequest): Observable<Page<Candidate>> {
    return this._http
      .get<Page<CandidateDTO>>(`${this.baseUrl()}`, {
        params: page.params(),
      })
      .pipe(
        map((response) => {
          const content = response.content.map(mapToCandidate);
          return new Page(page, content, response.totalPages);
        })
      );
  }

  findOne(id: string): Observable<CandidateDTO> {
    return this._http.get<CandidateDTO>(this.baseUrl(id));
  }

  create(candidate: CandidateDescriptor): Observable<any> {
    console.log(candidate);
    return this._http
      .post<Candidate>(this.baseUrl(), candidate, this.httpOptions)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 400) {
            this._toastr.error('Un candidat avec cet email existe déjà');
          }
          return of(false);
        })
      );
  }

  update(
    candidate: CandidateCreationDescriptor,
    candidateId: string
  ): Observable<Candidate> {
    return this._http.put<Candidate>(this.baseUrl(candidateId), candidate);
  }

  removeCandidates(candidatesIds: string[]): Observable<any> {
    return this._http.delete(this.baseUrl(), { body: candidatesIds }).pipe(
      map(() => {
        this._toastr.success('Candidat(s) supprimé(s) avec succès');
        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.error) {
          this._toastr.error(err.error.message, 'Une erreur est survenue');
        }
        return of(false);
      })
    );
  }

  sendEmailRemindCandidates(candidatesIds: string[]) {
    this._toastr.info('Envoi des emails en cours');
    return this._http.post(this.baseUrl(`remind`), candidatesIds).pipe(
      map(() => {
        this._toastr.clear();
        this._toastr.success('Mails de relances envoyés avec succès');
        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage =
          err.error?.message ??
          "Une erreur est survenue lors de l'envoi des emails";
        this._toastr.error(errorMessage, 'Une erreur est survenue');
        return of(false);
      })
    );
  }

  remove(candidateId: string): Observable<any> {
    return this._http.delete(this.baseUrl(candidateId));
  }

  uploadCandidates(
    groupId: string,
    assessmentFiles: FileList
  ): Observable<number> {
    if (assessmentFiles.length === 0) {
      return new Observable((subscriber) => subscriber.next(0));
    }
    const formData = new FormData();
    formData.append('csvFile', assessmentFiles.item(0));
    formData.append('groupId', groupId);

    return this._http.post<number>(this.baseUrl() + '/import', formData).pipe(
      tap((candidatsCreated) => {
        if (candidatsCreated > 0) {
          this._toastr.success(
            `Import traité avec succès. ${candidatsCreated} candidats créés.`,
            'Importation réussie'
          );
        } else {
          this._toastr.info(
            `Aucun nouveau candidat n'a été créé.`,
            'Ajout candidat à ce groupe'
          );
        }
      }),
      catchError((err: HttpErrorResponse) => {
        this._toastr.error(err.error.message, 'Une erreur est survenue');
        return of(0);
      })
    );
  }
}
